import React from "react";
import { ArrowLeft, ArrowRight, Archive, BriefcaseBusiness, FileImage, Globe2, Layers3, Sparkles } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import BrandStudioShellV2 from "@/components/brandStudio/BrandStudioShellV2";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import MyBrandWorkspaceV1 from "../../components/brandStudio/MyBrandWorkspaceV1";
import MyWorkshopV1 from "../../components/brandStudio/MyWorkshopV1";
import { brandStepUrl } from "../../lib/myBrandPresentationV1.mjs";

const POSSIBILITIES = [
  ["Фирменный стиль", "Логотип, цвет, типографика, графический язык и правила", Sparkles],
  ["Социальные сети", "Публикации, истории, обложки, серии и визуальные кампании", FileImage],
  ["Для бизнеса", "Прайсы, презентации, сертификаты, меню и документы", BriefcaseBusiness],
  ["Для клиента", "Карточки, предложения, инструкции и персональные материалы", Layers3],
  ["Печать и реклама", "Открытки, листовки, плакаты, наклейки и файлы для печати", Archive],
  ["Упаковка и физические носители", "Этикетки, коробки, бирки, ленты и производственные файлы", Layers3],
  ["Цифровые материалы", "PDF, презентации, обложки, баннеры и материалы для экранов", FileImage],
  ["Сайты", "Визитки, портфолио, лендинги, страницы продуктов и запусков", Globe2],
  ["Создать своё", "Опиши задачу своими словами — формат не обязан быть в каталоге", Sparkles],
];

const CATEGORY_FORMATS = [{"id":"identity","formats":[["Логотипы",brandStepUrl("logo", true),"logo"],["Цветовая система",brandStepUrl("visual-style", true),"palette"],["Типографика",brandStepUrl("typography", true),"typography"]]},{"id":"social","formats":[["Публикации"],["Истории"],["Обложки"]]},{"id":"business","formats":[["Прайсы"],["Презентации"],["Сертификаты"],["Меню"],["Документы"]]},{"id":"client","formats":[["Благодарственная карточка","/brand-studio/thank-you","thank-you"],["Хранение и подача"],["Инструкции"]]},{"id":"print","formats":[["Открытки"],["Листовки"],["Плакаты"],["Наклейки"]]},{"id":"packaging","formats":[["Бирка","/brand-studio/tag","tag"],["Этикетки"],["Коробки"],["Ленты"],["Производственные файлы"]]},{"id":"digital","formats":[["PDF"],["Презентации"],["Обложки"],["Баннеры"]]},{"id":"sites","formats":[["Сайт-визитка"],["Портфолио"],["Лендинг"]]},{"id":"own","formats":[]}];

const formatsFor = (title) => CATEGORY_FORMATS[POSSIBILITIES.findIndex(item => item[0] === title)];

const CONFIG = {
  workshop: {
    activeSpace: "workshop",
    eyebrow: "МОЯ МАСТЕРСКАЯ",
    title: "Всё, что ты создаёшь",
    text: "Материалы, проекты, свои шаблоны, недавние работы, черновики и архив будут жить здесь.",
    emptyTitle: "Мастерская пока пустая",
    emptyText: "Как только ты создашь первый материал, он появится здесь вместе с версиями, черновиками и проектами.",
  },
};

export default function BrandStudioSpaceV2({ space }) {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = POSSIBILITIES.find(([title]) => formatsFor(title).id === searchParams.get("category"))?.[0] || null;
  const chooseCategory = (title) => setSearchParams(title ? { category: formatsFor(title).id } : {});

  if (space === "my-brand") return <BrandStudioShellV2 activeSpace="my-brand"><MyBrandWorkspaceV1 /></BrandStudioShellV2>;
  if (space === "workshop") return <BrandStudioShellV2 activeSpace="workshop"><MyWorkshopV1 /></BrandStudioShellV2>;

  if (space === "possibilities") {
    return (
      <BrandStudioShellV2 activeSpace="create-material">
        <div className="mx-auto max-w-[1120px] pb-14" data-testid="brand-studio-possibilities-v2">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D53]">СОЗДАТЬ МАТЕРИАЛ</div>
            <h1 className="mt-2 font-serif text-[38px] leading-[1.02] text-[#2B211A] md:text-[50px]">
              Что будем создавать?
            </h1>
            <p className="mt-3 max-w-2xl text-[14px] leading-6 text-[#655141]">
              Выбери раздел и нужный формат. Работы сохраняются в «Моей мастерской».
            </p>
          </div>

          {selectedCategory ? (
            <section className="mt-8" data-testid="brand-studio-category-detail">
              <button type="button" data-testid="brand-studio-category-back" onClick={() => chooseCategory(null)} className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#D2C1AA] px-4 text-sm text-[#4A3728]"><ArrowLeft size={15} /> Все разделы</button>
              <h2 className="font-serif text-[31px] text-[#2B211A]">{selectedCategory}</h2>
              {formatsFor(selectedCategory).formats.length ? <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {formatsFor(selectedCategory).formats.map(([label, route, testId]) => <div key={label} className="flex min-h-[132px] flex-col justify-between rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5">
                  <h3 className="font-serif text-[24px] text-[#2B211A]">{label}</h3>
                  {route ? <button type="button" data-testid={"brand-studio-format-" + testId} onClick={() => nav(route)} className="mt-4 inline-flex min-h-10 items-center gap-2 self-start rounded-full bg-[#3D2A20] px-4 text-sm text-white">{formatsFor(selectedCategory).id === "identity" ? "Открыть" : "Создать"} <ArrowRight size={14} /></button> : <span className="mt-4 text-xs text-[#796652]">В разработке</span>}
                </div>)}</div> : <div className="mt-5 rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-6"><p className="text-sm text-[#655141]">Свободное описание задачи словами или голосом появится после подключения общего редактора материалов.</p><p className="mt-3 text-xs text-[#796652]">В разработке</p></div>}
            </section>
          ) : <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {POSSIBILITIES.map(([title, note, Icon]) => <button key={title} type="button" data-testid={"brand-studio-category-" + formatsFor(title).id} onClick={() => chooseCategory(title)} className="min-h-[180px] rounded-[26px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 text-left transition hover:border-[#A78C72] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#796652]">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[#F1E6D6] text-[#765D46]"><Icon size={16} /></span>
                <h2 className="mt-6 font-serif text-[25px] text-[#2B211A]">{title}</h2><p className="mt-2 text-[13px] leading-5 text-[#77634F]">{note}</p>
              </button>)}
            </div>
            <div className="mt-6 rounded-[28px] border border-[#E3D5C3] bg-[#F3E9DB] p-5 md:p-7">
              <h2 className="font-serif text-[28px] text-[#2B211A]">Не видишь нужного формата?</h2>
              <p className="mt-2 text-[13.5px] leading-6 text-[#655141]">Свободное описание задачи словами или голосом появится позже. Сейчас инструмент в разработке.</p>
              <button type="button" onClick={() => chooseCategory("Создать своё")} className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D2C1AA] bg-[#FFFDF9] px-6 text-sm text-[#3D2A20]">Создать своё <ArrowRight size={15} /></button>
            </div>
          </>}
          <footer className="pt-10 pb-2">
            <BrandStudioSignature />
          </footer>
        </div>
      </BrandStudioShellV2>
    );
  }

  const config = CONFIG[space];
  return (
    <BrandStudioShellV2 activeSpace={config.activeSpace}>
      <div className="mx-auto max-w-[1120px] pb-14" data-testid={`brand-studio-${space}-v2`}>
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D53]">{config.eyebrow}</div>
          <h1 className="mt-2 font-serif text-[38px] leading-[1.02] text-[#2B211A] md:text-[50px]">{config.title}</h1>
          <p className="mt-3 max-w-2xl text-[14px] leading-6 text-[#655141]">{config.text}</p>
        </div>

        <section className="mt-8 rounded-[30px] border border-[#E3D5C3] bg-[#FFFDF9] p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#927A5B]">ТВОЁ ПРОСТРАНСТВО</div>
          <h2 className="mt-3 font-serif text-[30px] text-[#2B211A]">
            {config.emptyTitle}
          </h2>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-6 text-[#6B5540]">
            {config.emptyText}
          </p>
          <button
            type="button"
            onClick={() => nav("/brand-studio/create")}
            className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
          >
            Перейти в «Создать» <ArrowRight size={15} />
          </button>
        </section>

        <footer className="pt-10 pb-2">
          <BrandStudioSignature />
        </footer>
      </div>
    </BrandStudioShellV2>
  );
}
